import React, { useEffect, useState, useMemo } from "react"
import Echo from "laravel-echo"
import Pusher from "pusher-js"
import { initialState } from "api/rootReducer"
import { setNewConversations, setNewPostStatus, showCommentsBadge, showDmsBadge } from "api"
import { store } from "api/store"

/**
 * Pusher configuration
 */
const pusherConfig = {
  key: '19c8491012c202140d92',
  cluster: 'mt1',
  authEndpoint: `${initialState.api_host}broadcasting/auth`
}

export const channelEventHandlers = {
  "new_conversation_message": (status, data) => {
    if (status && data) {
      // store the sender id
      setNewConversations({ ...store.getState().new_conversations, [data.resource_data.conversation.sender_id]: data.resource_data.conversation })
    }
    showDmsBadge(status);
  },
  "brand_created": undefined,
  "brand_deleted": undefined,
  "post_deleted": undefined,
  "post_status": setNewPostStatus,
  "social_connected": undefined,
  "social_removed": undefined,
  "draft_mention": undefined,
  "draft_shared": undefined,
  "invite_accepted": undefined,
  "team_invite": undefined,
  "invite_rejected": undefined,
  "role_updated": undefined,
  "token_expired": undefined,
  "token_expiry_reminder": undefined,
  "new_social_comment": showCommentsBadge,
}


/**
 * Context for Channels
 */
const ChannelsContext = React.createContext(undefined)

/**
 * Channel Context Provider
 */
export function ChannelsProvider({
  children,
  authUser,
  authToken
}) {
  const [channels, setChannels] = useState(undefined)
  useEffect(() => {
      const channels = getChannels(pusherConfig, authToken);
      setChannels(channels)
      return () => {
         // disconnect from server and reset the channels
         channels.disconnect()
         setChannels(undefined)
      }
  }, [authUser, authToken])
  return (
    <ChannelsContext.Provider value={channels}>
      {children}
    </ChannelsContext.Provider>
  )
}

/**
 * Hook to use the channels provided via context
 */
export function useChannels() {
  const channels = React.useContext(ChannelsContext)
  return channels
}

/**
 * Use private channels
 * It simple return the useChannels with authenticated user bindings
 */ 
export function usePrivateChannels (authUserId) {
  const channels = useChannels();
  return useMemo(() => {
    return channels && channels.private("users." + authUserId)
  }, [channels, authUserId])
}
    
/**
 * Get the channels
 */
function getChannels(pusherConfig, authToken) {
  const client = new Pusher(pusherConfig.key, {
    cluster: pusherConfig.cluster,
    forceTLS: true,
    authEndpoint: pusherConfig.authEndpoint,
    auth: authToken ? {
      headers: {
        // pass the authorization token when using private channels
        Authorization: `Bearer ${authToken}`,
        Accept: 'application/json',
      },
    }: undefined,
  })

  const channels = new Echo({
    broadcaster: "pusher",
    client: client,
  })

  return channels;
}